// @file Helpers for Library related stuff
import { __ } from '@@/bits/intl'
import { LibraryMembershipRole, LibraryType } from '@@/enums'
import type { OnboardingTask, OnboardingTaskData } from '@@/types'

export const teamLibraryOnboardingDataByTask: Partial<Record<OnboardingTask, OnboardingTaskData>> = {
  addNewTeam: {
    key: 'addNewTeam',
    icon: 'group',
    taskStringOne: __('Add a new team'),
    taskStringTwo: __('Create a space for team members to make and share padlets.'),
    taskButtonText: __('Create new team'),
    taskButtonLabel: __('Create new team'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 1,
  },
  customizeTeamInfo: {
    key: 'customizeTeamInfo',
    icon: 'pencil_outline',
    taskStringOne: __('Customize team info'),
    taskStringTwo: __('Pick a great team name and avatar in Settings > Team info.'),
    taskButtonText: __('Team info'),
    taskButtonLabel: __('Go to team info settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 2,
  },
  addMembers: {
    key: 'addMembers',
    icon: 'add_account_outline',
    taskStringOne: __('Add team members'),
    taskStringTwo: __('Invite new members to your team in Settings > Members > Invite.'),
    taskButtonText: __('Members'),
    taskButtonLabel: __('Go to team members settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 3,
  },
  pickPlan: {
    key: 'pickPlan',
    icon: 'credit_outline',
    taskStringOne: __('Pick the best plan'),
    taskStringTwo: __('Explore team plan options in Settings > Billing.'),
    taskButtonText: __('Billing'),
    taskButtonLabel: __('Go to team billing settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 4,
  },
  makePrivateWall: {
    key: 'makePrivateWall',
    icon: 'password',
    taskStringOne: __('Make a private padlet'),
    taskStringTwo: __(
      'Change padlet privacy from Team only to Private by clicking Make > Share > Change privacy > Private.',
    ),
    taskButtonText: __('Make a padlet'),
    taskButtonLabel: __('Make a padlet'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 5,
  },
}

export const schoolLibraryOnboardingDataByTaskArray: OnboardingTaskData[] = [
  {
    key: 'addNewTeam', // addNewTeam is used across teams and schools
    icon: 'group',
    taskStringOne: __('Create a new school account'),
    taskStringTwo: __('Create a space for teachers and students to make new padlets.'),
    taskButtonText: __('Create new school'),
    taskButtonLabel: __('Create new school'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 1,
    hideTaskForAdmins: false,
  },
  {
    key: 'addAdmins',
    icon: 'add_account_outline',
    taskStringOne: __('Add admins'),
    taskStringTwo: __('Invite new admins in Settings > Members > Add admins.'),
    taskButtonText: __('Members'),
    taskButtonLabel: __('Go to school members settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 2,
    hideTaskForAdmins: true,
  },
  {
    key: 'addTeachersAndStudents',
    icon: 'add_account_outline',
    taskStringOne: __('Add teachers and students'),
    taskStringTwo: __('Invite new teachers and students in Settings > Members > Add teachers and students.'),
    taskButtonText: __('Members'),
    taskButtonLabel: __('Go to school members settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 3,
    hideTaskForAdmins: false,
  },
  {
    key: 'connectSchoolSso',
    icon: 'fingerprint',
    taskStringOne: __("Connect your organization's SSO"),
    taskStringTwo: __('Make logging in easier by setting up SSO for your organization'),
    taskButtonText: __('Access'),
    taskButtonLabel: __('Go to organization info access settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 4,
    hideTaskForAdmins: false,
  },
  {
    key: 'configurePermissions',
    icon: 'edit_account_outline',
    taskStringOne: __('Configure permissions'),
    taskStringTwo: __('Set up what teachers and students can do in Settings > Permissions.'),
    taskButtonText: __('Permissions'),
    taskButtonLabel: __('Go to school permissions settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 5,
    hideTaskForAdmins: false,
  },
  {
    key: 'configureConnections',
    icon: 'link',
    taskStringOne: __('Configure connections'),
    taskStringTwo: __('Connect your school to LMS and other services in Settings > Connections.'),
    taskButtonText: __('Connections'),
    taskButtonLabel: __('Go to school connections settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 6,
    hideTaskForAdmins: false,
  },
  {
    key: 'configureContentSafety',
    icon: 'warning',
    taskStringOne: __('Configure content safety'),
    taskStringTwo: __('Set up content moderation rules in Settings > Content.'),
    taskButtonText: __('Content'),
    taskButtonLabel: __('Go to school content safety settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 7,
    hideTaskForAdmins: false,
  },
]

export const classroomLibraryOnboardingDataByTask: Partial<Record<OnboardingTask, OnboardingTaskData>> = {
  addNewTeam: {
    key: 'addNewTeam',
    icon: 'group',
    taskStringOne: __('Add a new classroom'),
    taskStringTwo: __('Create a space for students and teachers to make and share padlets.'),
    taskButtonText: __('Create new classroom'),
    taskButtonLabel: __('Create new classroom'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 1,
  },
  customizeTeamInfo: {
    key: 'customizeTeamInfo',
    icon: 'pencil_outline',
    taskStringOne: __('Customize classroom info'),
    taskStringTwo: __('Pick a great classroom name and avatar in Settings > Classroom info.'),
    taskButtonText: __('Classroom info'),
    taskButtonLabel: __('Go to classroom info settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 2,
  },
  addMembers: {
    key: 'addMembers',
    icon: 'add_account_outline',
    taskStringOne: __('Add students'),
    taskStringTwo: __('Invite new students to your classroom in Settings > Members > Invite.'),
    taskButtonText: __('Members'),
    taskButtonLabel: __('Go to classroom members settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 3,
  },
  pickPlan: {
    key: 'pickPlan',
    icon: 'credit_outline',
    taskStringOne: __('Upgrade your classroom'),
    taskStringTwo: __('Keep creating padlets in your classroom when your trial ends by upgrading.'),
    taskButtonText: __('Billing'),
    taskButtonLabel: __('Go to classroom billing settings'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 4,
  },
  makePrivateWall: {
    key: 'makePrivateWall',
    icon: 'password',
    taskStringOne: __('Make a private padlet'),
    taskStringTwo: __('Make something just for you by clicking Make > Share > Change privacy > Private.'),
    taskButtonText: __('Make a padlet'),
    taskButtonLabel: __('Make a padlet'),
    isTaskDone: false,
    taskDoneAt: null,
    order: 5,
  },
}

export function getOnboardingDataByTask(
  task: OnboardingTask,
  libraryType: LibraryType,
  isLibraryOwner: boolean,
): OnboardingTaskData | undefined {
  // School libraries show a different set of tasks for admins and non-admins
  if (libraryType === LibraryType.School) {
    let filteredTasks: OnboardingTaskData[] = schoolLibraryOnboardingDataByTaskArray
    if (!isLibraryOwner) {
      filteredTasks = schoolLibraryOnboardingDataByTaskArray.filter((item) => item.hideTaskForAdmins !== true)
    }

    const reorderedTasks = filteredTasks.map((item, index) => ({
      ...item,
      order: index + 1, // Assign sequential order starting from 1
    }))

    return reorderedTasks.find((item) => item.key === task)
  }

  const libraryTypeToLibraryOnboardingDataByTask = {
    [LibraryType.Team]: teamLibraryOnboardingDataByTask,
    [LibraryType.Classroom]: classroomLibraryOnboardingDataByTask,
  }
  return libraryTypeToLibraryOnboardingDataByTask[libraryType][task]
}

export function getLibraryMembershipRoleTranslation(role: string): string {
  return {
    [LibraryMembershipRole.Owner]: __('Owner'),
    [LibraryMembershipRole.Admin]: __('Admin'),
    [LibraryMembershipRole.Maker]: __('Maker'),
    [LibraryMembershipRole.Teacher]: __('Teacher'),
    [LibraryMembershipRole.Student]: __('Student'),
    [LibraryMembershipRole.Contributor]: __('Contributor'),
  }[role as LibraryMembershipRole]
}

export function getLibraryMembershipRoleTranslationByLibrary(
  role: LibraryMembershipRole,
  libraryType: LibraryType = LibraryType.Team,
): string {
  return {
    [LibraryType.Classroom]: {
      [LibraryMembershipRole.Owner]: __('Teacher (owner)'),
      [LibraryMembershipRole.Teacher]: __('Teacher'),
      [LibraryMembershipRole.Student]: __('Student'),
    },
    [LibraryType.Team]: {
      [LibraryMembershipRole.Owner]: __('Owner'),
      [LibraryMembershipRole.Admin]: __('Admin'),
      [LibraryMembershipRole.Maker]: __('Maker'),
      [LibraryMembershipRole.Contributor]: __('Contributor'),
    },
    [LibraryType.School]: {
      [LibraryMembershipRole.Owner]: __('Owner'),
      [LibraryMembershipRole.Admin]: __('Admin'),
      [LibraryMembershipRole.Teacher]: __('Teacher'),
      [LibraryMembershipRole.Student]: __('Student'),
    },
  }[libraryType][role]
}
